import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/** Marathon distance in kilometers */
export const MARATHON_DISTANCE_KM = 42.195;

export interface Challenge {
  id: string;
  creatorAddress: string;
  name: string;
  description: string | null;
  type: string; // "run" | "walk" | "cycle" | "marathon"
  dailyGoal: number;
  totalDistanceGoal: number;
  durationDays: number;
  requiredDays: number;
  stakeAmount: number;
  totalPrizePool: number;
  startDate: string; // "YYYY-MM-DD" format
  endDate: string; // "YYYY-MM-DD" format
  timezone: string; // Challenge timezone (e.g., "America/New_York")
  status: string;
  createdAt: string;
  // Marathon-specific fields
  targetDurationSeconds?: number | null;
  isOffChain?: boolean;
  participantCount?: number;
  participants?: Participant[];
  // Current user's participant data (from challenge detail endpoint)
  currentUserParticipant?: Participant | null;
  // User's own progress (from my challenges endpoints)
  myProgress?: Participant | null;
  // Pagination info for participants list
  pagination?: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
  creator?: {
    id: string;
    email: string;
    username: string | null;
    walletAddress: string;
    avatarUrl?: string | null;
  };
}

export interface Participant {
  rank?: number;
  challengeId?: string;
  walletAddress: string;
  stakePaid?: number;
  joinedAt?: string;
  status?: string;
  totalRunDistance?: number;
  totalRunDuration?: number;
  daysCompleted?: number;
  daysLogged?: number;
  currentStreak?: number;
  longestStreak?: number;
  isWinner?: boolean;
  payoutAmount?: number;
  user?: {
    id: string;
    username: string | null;
    avatarUrl: string | null;
    walletAddress?: string;
  };
}

export interface CreateChallengeRequest {
  id: string; // Contract address or unique ID for off-chain challenges
  creatorAddress: string; // From Privy auth
  name: string;
  description?: string | null;
  type: string; // "run" | "walk" | "cycle" | "marathon"
  dailyGoal: number; // In kilometers (fixed at 42.195 for marathon)
  totalDistanceGoal: number; // dailyGoal × durationDays
  durationDays: number; // Must be multiple of 7, or 1 for marathon
  requiredDays: number; // Min days to complete
  stakeAmount: number; // In USD/USDC
  startDate: string; // Date string in "YYYY-MM-DD" format
  endDate: string; // Date string in "YYYY-MM-DD" format
  timezone: string; // Creator's timezone (e.g., "America/New_York")
  // Marathon-specific fields
  targetDurationSeconds?: number; // For marathon: max time to complete (in seconds)
  isOffChain?: boolean; // True for marathon (no smart contract)
}

export const challengesApi = {
  // Get all challenges
  getChallenges: async (): Promise<Challenge[]> => {
    const response = await apiClient.get(API_ENDPOINTS.CHALLENGES.LIST);
    return response.data.challenges || []; // Extract challenges array from response
  },

  // Get challenge by ID
  getChallenge: async (id: string): Promise<Challenge> => {
    const response = await apiClient.get(API_ENDPOINTS.CHALLENGES.GET_BY_ID(id));
    return response.data.challenge; // Extract challenge from response wrapper
  },

  // Create a new challenge
  createChallenge: async (data: CreateChallengeRequest): Promise<Challenge> => {
    const response = await apiClient.post(API_ENDPOINTS.CHALLENGES.CREATE, data);
    return response.data.challenge; // Extract challenge from response wrapper
  },

  // Join a challenge
  joinChallenge: async (id: string, stakePaid: number): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHALLENGES.JOIN(id), { stakePaid });
  },

  // Leave a challenge
  leaveChallenge: async (id: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHALLENGES.LEAVE(id));
  },

  // Get leaderboard for a challenge
  getLeaderboard: async (id: string): Promise<Participant[]> => {
    const response = await apiClient.get(API_ENDPOINTS.CHALLENGES.LEADERBOARD(id));
    return response.data;
  },

  // Get user's active challenges (startDate <= now AND endDate >= now)
  getMyActiveChallenges: async (): Promise<Challenge[]> => {
    const response = await apiClient.get(API_ENDPOINTS.CHALLENGES.MY_ACTIVE);
    return response.data.challenges || [];
  },

  // Get user's upcoming challenges (startDate > now)
  getMyUpcomingChallenges: async (): Promise<Challenge[]> => {
    const response = await apiClient.get(API_ENDPOINTS.CHALLENGES.MY_UPCOMING);
    return response.data.challenges || [];
  },

  // Get user's completed challenges (endDate < now)
  getMyCompletedChallenges: async (): Promise<Challenge[]> => {
    const response = await apiClient.get(API_ENDPOINTS.CHALLENGES.MY_COMPLETED);
    return response.data.challenges || [];
  },
};
